import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout } from '../../../widgets/Layout';
import { commonStyles, playerBarStyles, theme } from '../../../shared/lib/styles';
import { CLASS_NUM_MAP, DISK_TRACK_COUNTS, GRADES } from '../../../shared/lib/audioposobie/constants';
import AddToPlaylistModal from '../../../features/playlists/ui/AddToPlaylistModal';
import { TABLE_CONTENT_4, TABLE_CONTENT_5, TABLE_CONTENT_6, TABLE_CONTENT_7 } from './tableContent';

const ADDED_TO_PLAYLIST_INDICATOR_MS = 3000;

const AudioposobiePage = () => {
    const location = useLocation();
    const [activeGrade, setActiveGrade] = useState(null);
    const [activeDisk, setActiveDisk] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [playingTrackNum, setPlayingTrackNum] = useState(null);
    const [withCommentary, setWithCommentary] = useState(false);
    const [playNext, setPlayNext] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [addToPlaylistTrack, setAddToPlaylistTrack] = useState(null);
    const [addedToPlaylistKeys, setAddedToPlaylistKeys] = useState(() => new Set());
    const audioRef = useRef(null);
    const addedClearTimersRef = useRef(new Map());

    useEffect(() => {
        const state = location.state;
        if (!state?.grade) return;
        const grade = Number(state.grade);
        const diskNum = state.disk_num ? Number(state.disk_num) : 1;
        setActiveGrade(grade);
        setActiveDisk({
            id: `disk_${diskNum}`,
            lessonNum: diskNum,
            title: `раздел № ${diskNum}`,
        });
        setCurrentTrack(null);
    }, [location.key, location.state]);

    const getPlaylistTrackKey = (grade, diskNum, trackNum) =>
        `${grade}-${diskNum}-${trackNum}`;

    const isAddedToPlaylist = (trackNum) => {
        if (!classNum || !diskNum || trackNum == null) return false;
        return addedToPlaylistKeys.has(
            getPlaylistTrackKey(Number(classNum), diskNum, trackNum)
        );
    };

    const markAddedToPlaylist = (track) => {
        if (!track) return;
        const key = getPlaylistTrackKey(track.grade, track.disk_num, track.track_num);

        const existingTimer = addedClearTimersRef.current.get(key);
        if (existingTimer) clearTimeout(existingTimer);

        setAddedToPlaylistKeys((prev) => new Set(prev).add(key));

        const timerId = setTimeout(() => {
            setAddedToPlaylistKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            addedClearTimersRef.current.delete(key);
        }, ADDED_TO_PLAYLIST_INDICATOR_MS);

        addedClearTimersRef.current.set(key, timerId);
    };

    useEffect(() => {
        const timers = addedClearTimersRef.current;
        return () => {
            timers.forEach((timerId) => clearTimeout(timerId));
            timers.clear();
        };
    }, []);

    useEffect(() => {
        const onScroll = () => setShowScrollTop(window.scrollY > 300);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (currentTrack?.src && audioRef.current) {
            audioRef.current.src = currentTrack.src;
            audioRef.current.play().catch(() => {});
        }
    }, [currentTrack?.src]);

    const classNum = activeGrade ? CLASS_NUM_MAP[activeGrade] : null;
    const diskNum = activeDisk?.lessonNum ?? null;
    const hasContentTable =
        (classNum === '5' && diskNum && TABLE_CONTENT_5[diskNum]) ||
        (classNum === '4' && diskNum && TABLE_CONTENT_4[diskNum]) ||
        (classNum === '6' && diskNum && TABLE_CONTENT_6[diskNum]) ||
        (classNum === '7' && diskNum && TABLE_CONTENT_7[diskNum]);
    const contentTable =
        classNum === '5' && diskNum
            ? TABLE_CONTENT_5[diskNum]
            : classNum === '4' && diskNum
              ? TABLE_CONTENT_4[diskNum]
              : classNum === '6' && diskNum
                ? TABLE_CONTENT_6[diskNum]
                : classNum === '7' && diskNum
                  ? TABLE_CONTENT_7[diskNum]
                  : null;
    // Комментарий автора (трек N-1 перед N) для всех классов с контент-таблицей (4, 5 и т.д.)
    const hasCommentaryTracks = !!hasContentTable;
    const trackCount = classNum && diskNum ? DISK_TRACK_COUNTS[classNum]?.[diskNum] : 0;
    // Множество номеров треков, которые есть в таблице как контент (если N-1 в этом множестве, у трека N нет отдельного комментария)
    const contentTrackNumbers = useMemo(
        () =>
            contentTable
                ? new Set(contentTable.flatMap((work) => work.tracks.map((t) => t.trackNum)))
                : new Set(),
        [contentTable]
    );
    // Плоский список треков для текущего диска (для переключения пред./след.)
    const trackList = useMemo(() => {
        if (contentTable) {
            return contentTable.flatMap((work) =>
                work.tracks.map((t) => ({ trackNum: t.trackNum, title: work.authorTitle }))
            );
        }
        if (trackCount) {
            return Array.from({ length: trackCount }, (_, i) => ({
                trackNum: i + 1,
                title: `Трек ${i + 1}`,
            }));
        }
        return [];
    }, [contentTable, trackCount]);
    // Список всех произведений по всем дискам (для 4, 5, 6, 7 класса) — для перехода между произведениями разных дисков
    const allWorksList = useMemo(() => {
        const buildFromTable = (tableByDisk, filterTracks = false) =>
            [].concat(
                ...Array.from({ length: 10 }, (_, i) => {
                    const d = i + 1;
                    const table = tableByDisk[d];
                    if (!table) return [];
                    const works = filterTracks
                        ? table.filter((work) => work.tracks && work.tracks.length > 0)
                        : table;
                    return works.map((work) => ({
                        diskNum: d,
                        work,
                        trackNum: work.tracks?.[0]?.trackNum,
                        title: work.authorTitle,
                    }));
                })
            ).filter((entry) => entry.trackNum != null);

        if (classNum === '5') return buildFromTable(TABLE_CONTENT_5);
        if (classNum === '4') return buildFromTable(TABLE_CONTENT_4, true);
        if (classNum === '6') return buildFromTable(TABLE_CONTENT_6, true);
        if (classNum === '7') return buildFromTable(TABLE_CONTENT_7, true);
        return [];
    }, [classNum]);
    const currentWorkIndex =
        playingTrackNum != null && diskNum != null && allWorksList.length > 0
            ? allWorksList.findIndex(
                  (entry) =>
                      entry.diskNum === diskNum &&
                      entry.work.tracks.some((t) => t.trackNum === playingTrackNum)
              )
            : -1;
    // Чекбокс «С комментарием автора» блокируем, если у текущего трека нет комментария (предыдущий трек уже в таблице)
    const commentaryCheckboxDisabled =
    !hasCommentaryTracks ||
    (playingTrackNum != null && contentTrackNumbers.has(playingTrackNum - 1));
    // При воспроизведении комментария считаем индекс по основному треку (nextTrack), чтобы Prev/Next работали
    const currentIndex = currentTrack
    ? currentTrack.nextTrack
        ? trackList.findIndex((t) => t.trackNum === currentTrack.nextTrack.trackNum)
        : trackList.findIndex((t) => t.trackNum === currentTrack.trackNum)
    : -1;
    const disks = activeGrade
        ? Array.from({ length: 10 }, (_, i) => ({
              id: `disk_${i + 1}`,
              lessonNum: i + 1,
              title: `раздел № ${i + 1}`,
          }))
        : [];
    const canGoPrev = currentIndex > 0 || (allWorksList.length > 0 && currentWorkIndex > 0);
    const canGoNext =
        currentIndex >= 0 && currentIndex < trackList.length - 1 ||
        (allWorksList.length > 0 && currentWorkIndex >= 0 && currentWorkIndex < allWorksList.length - 1);
    const grades = GRADES;

    const openAddToPlaylist = (trackNum, title) => {
        if (!classNum || !diskNum) return;
        setAddToPlaylistTrack({
            grade: Number(classNum),
            disk_num: diskNum,
            track_num: trackNum,
            title: title || `Трек ${trackNum}`,
        });
    };

    const renderAddToPlaylistControl = (trackNum, title, variant = 'table') => {
        if (!classNum || !diskNum) return null;

        if (isAddedToPlaylist(trackNum)) {
            if (variant === 'player') {
                return (
                    <span style={styles.addPlaylistBtnPlayerAdded} title="Добавлено в плейлист">
                        ✓ В плейлисте
                    </span>
                );
            }
            return (
                <span style={styles.addPlaylistBtnAdded} title="Добавлено в плейлист">
                    ✓
                </span>
            );
        }

        if (variant === 'player') {
            return (
                <button
                    type="button"
                    onClick={() =>
                        openAddToPlaylist(
                            trackNum,
                            title
                        )
                    }
                    style={styles.addPlaylistBtnPlayer}
                    title="Добавить в плейлист"
                >
                    + В плейлист
                </button>
            );
        }

        return (
            <button
                type="button"
                title="Добавить в плейлист"
                onClick={(e) => {
                    e.stopPropagation();
                    openAddToPlaylist(trackNum, title);
                }}
                style={styles.addPlaylistBtn}
            >
                +
            </button>
        );
    };

    const normalizeLabel = (s) => (s ?? '').toString().replace(/\s+/g, ' ').trim();

    const formatPlayerTitle = (title, sectionName) => {
        const t = normalizeLabel(title);
        const s = normalizeLabel(sectionName);
        if (!s) return t;
        if (t && t === s) return t;
        // Не дублировать: если полное название уже содержит текст раздела — не добавлять " — раздел"
        if (t && s && t.includes(s)) return t;
        return t ? `${t} — ${s}` : s;
    };

    const getAudioSrcForDisk = (dNum, tNum) =>
        classNum && dNum ? `/audio/${classNum}class/disk${dNum}/Track${tNum}.MP3` : null;

    const getAudioSrc = (trackNum) => {
        if (!classNum || !diskNum) return null;
        return `/audio/${classNum}class/disk${diskNum}/Track${trackNum}.MP3`;
    };

    // skipCommentary = true при переходе с комментария на основной трек (чтобы не играть комментарий повторно)
    // options.useCommentary — переопределить состояние чекбокса (для перезапуска при смене чекбокса)
    const playTrackByItem = (item, skipCommentary = false, options = {}) => {
        const src = getAudioSrc(item.trackNum);
        if (!src) return;
        const useCommentary = options.useCommentary !== undefined ? options.useCommentary : withCommentary;
        const playCommentaryFirst =
            useCommentary &&
            hasCommentaryTracks &&
            item.trackNum > 1 &&
            !skipCommentary &&
            !contentTrackNumbers.has(item.trackNum - 1);
        if (playCommentaryFirst) {
            const commentaryTrackNum = item.trackNum - 1;
            const commentarySrc = getAudioSrc(commentaryTrackNum);
            if (commentarySrc) {
                setCurrentTrack({
                    src: commentarySrc,
                    trackNum: commentaryTrackNum,
                    title: 'Комментарий автора',
                    isCommentary: true,
                    nextTrack: item,
                });
                setPlayingTrackNum(item.trackNum); // в таблице подсвечиваем основной трек
                return;
            }
        }
        setCurrentTrack({
            src,
            trackNum: item.trackNum,
            title: item.title,
            sectionName: item.sectionName,
        });
        setPlayingTrackNum(item.trackNum);
    };

    const handlePlayTrack = (trackNum, title, sectionName) => {
        if (playingTrackNum === trackNum && currentTrack && audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current.play().catch(() => {});
            } else {
                audioRef.current.pause();
            }
            return;
        }
        const item = trackList.find((t) => t.trackNum === trackNum);
        if (item) playTrackByItem({ ...item, title: title ?? item.title, sectionName });
    };

    const goToPrevWork = () => {
        if (currentWorkIndex <= 0 || allWorksList.length === 0 || disks.length === 0) return;
        const entry = allWorksList[currentWorkIndex - 1];
        setActiveDisk(disks[entry.diskNum - 1]);
        setCurrentTrack({
            src: getAudioSrcForDisk(entry.diskNum, entry.trackNum),
            trackNum: entry.trackNum,
            title: entry.title,
        });
        setPlayingTrackNum(entry.trackNum);
    };

    const goToNextWork = () => {
        if (currentWorkIndex < 0 || currentWorkIndex >= allWorksList.length - 1 || disks.length === 0) return;
        const entry = allWorksList[currentWorkIndex + 1];
        setActiveDisk(disks[entry.diskNum - 1]);
        setCurrentTrack({
            src: getAudioSrcForDisk(entry.diskNum, entry.trackNum),
            trackNum: entry.trackNum,
            title: entry.title,
        });
        setPlayingTrackNum(entry.trackNum);
    };

    const handlePrev = () => {
        if (currentIndex > 0) playTrackByItem(trackList[currentIndex - 1]);
        else if (allWorksList.length > 0 && currentWorkIndex > 0) goToPrevWork();
    };

    const handleNext = () => {
        if (currentIndex >= 0 && currentIndex < trackList.length - 1)
            playTrackByItem(trackList[currentIndex + 1]);
        else if (allWorksList.length > 0 && currentWorkIndex >= 0 && currentWorkIndex < allWorksList.length - 1)
            goToNextWork();
    };

    return (
        <Layout>
            <div style={{ ...styles.container, paddingBottom: currentTrack ? 140 : undefined }}>
                <h1 style={styles.title}>Аудиопособие</h1>

                {!activeGrade ? (
                    <>
                        <Link to="/dashboard" style={styles.backLink}>
                            ← На главную
                        </Link>
                        <div style={styles.gradeGrid}>
                        {grades.map((grade) => (
                            <button
                                key={grade}
                                onClick={() => {
                                    setActiveGrade(grade);
                                    setActiveDisk({
                                        id: 'disk_1',
                                        lessonNum: 1,
                                        title: 'раздел № 1',
                                    });
                                    setCurrentTrack(null);
                                }}
                                style={styles.gradeButton}
                            >
                                {grade}
                            </button>
                        ))}
                        </div>
                    </>
                ) : (
                    <div>
                        <button
                            type="button"
                            onClick={() => {
                                setActiveGrade(null);
                                setActiveDisk(null);
                                setCurrentTrack(null);
                            }}
                            style={styles.backLinkBtn}
                            title="Назад к выбору класса"
                        >
                            ← Назад
                        </button>
                        <div style={styles.diskSwitcherWrap}>
                            <div style={styles.diskSwitcher}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (diskNum > 1) {
                                            setActiveDisk(disks[diskNum - 2]);
                                            setCurrentTrack(null);
                                        }
                                    }}
                                    disabled={diskNum <= 1}
                                    style={{
                                        ...styles.diskSwitcherBtn,
                                        ...styles.diskSwitcherArrowBtn,
                                        ...(diskNum <= 1 ? styles.diskSwitcherBtnDisabled : {}),
                                    }}
                                    title="Предыдущий диск"
                                >
                                    ‹
                                </button>
                                {disks.map((disk) => (
                                    <button
                                        key={disk.id}
                                        type="button"
                                        onClick={() => {
                                            setActiveDisk(disk);
                                            setCurrentTrack(null);
                                        }}
                                        style={{
                                            ...styles.diskSwitcherBtn,
                                            ...(diskNum === disk.lessonNum ? styles.diskSwitcherBtnActive : {}),
                                        }}
                                        title={disk.title}
                                    >
                                        {disk.lessonNum}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (diskNum < 10) {
                                            setActiveDisk(disks[diskNum]);
                                            setCurrentTrack(null);
                                        }
                                    }}
                                    disabled={diskNum >= 10}
                                    style={{
                                        ...styles.diskSwitcherBtn,
                                        ...styles.diskSwitcherArrowBtn,
                                        ...(diskNum >= 10 ? styles.diskSwitcherBtnDisabled : {}),
                                    }}
                                    title="Следующий диск"
                                >
                                    ›
                                </button>
                            </div>
                        </div>
                        <h2 style={styles.gradeTitle}>
                            {activeGrade} — {activeDisk.title}
                        </h2>
                        {classNum === '4' && (
                            <p style={styles.subtitle}>
                                Введение в мир музыки. Составитель М. Б. Кушнир
                            </p>
                        )}
                        {classNum === '5' && (
                            <p style={styles.subtitle}>
                                Музыка зарубежных стран. Составитель М. Б. Кушнир
                            </p>
                        )}
                        {classNum === '6' && (
                            <p style={styles.subtitle}>
                                Русская музыка. Составитель М. Б. Кушнир
                            </p>
                        )}
                        {classNum === '7' && (
                            <p style={styles.subtitle}>
                                Отечественная музыка XX века. Составитель М. Б. Кушнир
                            </p>
                        )}

                        <div style={styles.tableWrapper}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>№</th>
                                        <th style={styles.th}>Автор, название произведения</th>
                                        <th style={styles.th}>№ трека</th>
                                        <th style={styles.th}>Время звучания</th>
                                        <th style={styles.th}>Исполнитель</th>
                                        <th style={styles.th}> </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contentTable ? (
                                        contentTable.map((work, workIndex) => {
                                            const hasDetailedRows = !!work.rows;
                                            const rows = work.rows || work.tracks.map((t) => ({ type: 'track', ...t }));
                                            const rowCount = rows.length;
                                            return rows.map((row, rowIndex) => {
                                                const isTrackRow = row.type === 'track';
                                                const isTitleRow = row.type === 'title';
                                                const isSubheadingRow = row.type === 'subheading';
                                                const trackNum = isTrackRow ? row.trackNum : null;
                                                const isPlaying = isTrackRow && playingTrackNum === trackNum;
                                                const authorContent =
                                                    isTitleRow
                                                        ? work.authorTitle
                                                        : isSubheadingRow
                                                            ? row.section
                                                            : isTrackRow
                                                                ? (hasDetailedRows && rowCount > 1 ? (row.section || work.authorTitle) : work.authorTitle)
                                                                : work.authorTitle;
                                                const authorIndent = (isTrackRow && row.indentLevel) || isSubheadingRow ? 12 + (row.indentLevel ?? (isSubheadingRow ? 1 : 0)) * 20 : 12;
                                                const showAuthorCell = hasDetailedRows || rowIndex === 0;
                                                const rowKey = work.rows ? `r-${workIndex}-${rowIndex}` : `r-${workIndex}-${row.trackNum}`;
                                                return (
                                                    <tr
                                                        key={rowKey}
                                                        onClick={isTrackRow ? () => handlePlayTrack(trackNum, work.authorTitle, row.section || undefined) : undefined}
                                                        style={{
                                                            ...styles.tr,
                                                            ...(isTrackRow && isPlaying ? styles.trPlaying : {}),
                                                            cursor: isTrackRow ? 'pointer' : 'default',
                                                        }}
                                                    >
                                                        {rowIndex === 0 && (
                                                            <td
                                                                rowSpan={rowCount}
                                                                style={styles.td}
                                                            >
                                                                {work.tracks && work.tracks.length === 0 ? '—' : `${work.num}.`}
                                                            </td>
                                                        )}
                                                        {showAuthorCell && (
                                                            <td
                                                                rowSpan={hasDetailedRows ? undefined : rowCount}
                                                                style={{
                                                                    ...styles.tdAuthor,
                                                                    ...(hasDetailedRows ? { paddingLeft: authorIndent } : {}),
                                                                }}
                                                            >
                                                                {authorContent}
                                                            </td>
                                                        )}
                                                        <td style={styles.td}>
                                                            {isTrackRow ? trackNum : ''}
                                                        </td>
                                                        <td style={styles.td}>
                                                            {isTrackRow ? row.duration : ''}
                                                        </td>
                                                        {rowIndex === 0 && (
                                                            <td
                                                                rowSpan={rowCount}
                                                                style={styles.tdPerformer}
                                                            >
                                                                {work.performer}
                                                            </td>
                                                        )}
                                                        <td style={styles.td}>
                                                            {isTrackRow
                                                                ? renderAddToPlaylistControl(
                                                                      trackNum,
                                                                      work.authorTitle
                                                                  )
                                                                : null}
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })
                                    ) : trackCount ? (
                                        Array.from(
                                            { length: trackCount },
                                            (_, i) => {
                                                const localNum = i + 1;
                                                return (
                                                    <tr
                                                        key={localNum}
                                                        onClick={() =>
                                                            handlePlayTrack(localNum, `Трек ${localNum}`)
                                                        }
                                                        style={{
                                                            ...styles.tr,
                                                            ...(playingTrackNum === localNum
                                                                ? styles.trPlaying
                                                                : {}),
                                                        }}
                                                    >
                                                        <td style={styles.td}>{localNum}</td>
                                                        <td style={styles.tdAuthor}>
                                                            Трек {localNum}
                                                        </td>
                                                        <td style={styles.td}>{localNum}</td>
                                                        <td style={styles.td}>—</td>
                                                        <td style={styles.tdPerformer}>—</td>
                                                        <td style={styles.td}>
                                                            {renderAddToPlaylistControl(
                                                                localNum,
                                                                `Трек ${localNum}`
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        )
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Плеер вторым слоем внизу экрана */}
            {currentTrack && (
                <div style={styles.playerFixed}>
                    <div style={styles.playerFixedInner}>
                        <button
                            type="button"
                            onClick={handlePrev}
                            disabled={!canGoPrev}
                            style={{
                                ...styles.playerNavBtn,
                                ...(!canGoPrev && styles.playerNavBtnDisabled),
                            }}
                            title={allWorksList.length > 0 ? 'Предыдущий трек / произведение' : 'Предыдущий трек'}
                        >
                            ‹
                        </button>
                        <div style={styles.playerFixedContent}>
                            <div style={styles.playerTitleRow}>
                                <div
                                    style={{
                                        ...styles.playerTitle,
                                        ...(currentTrack.title === 'Комментарий автора' && styles.playerTitleCommentary),
                                    }}
                                    title={formatPlayerTitle(currentTrack.title, currentTrack.sectionName)}
                                >
                                    {formatPlayerTitle(currentTrack.title, currentTrack.sectionName)}
                                </div>
                                {classNum && diskNum && playingTrackNum != null &&
                                    renderAddToPlaylistControl(
                                        playingTrackNum,
                                        currentTrack.nextTrack?.title || currentTrack.title,
                                        'player'
                                    )}
                            </div>
                            <div style={styles.playerCheckboxRow}>
                                <label style={styles.playerCheckbox}>
                                    <input
                                        type="checkbox"
                                        checked={withCommentary}
                                        onChange={() => {
                                            const nextCommentary = !withCommentary;
                                            setWithCommentary(nextCommentary);
                                            if (currentTrack && playingTrackNum != null) {
                                                const item = trackList.find((t) => t.trackNum === playingTrackNum);
                                                if (item) {
                                                    const withSection =
                                                        currentTrack.trackNum === item.trackNum || (currentTrack.nextTrack && currentTrack.nextTrack.trackNum === item.trackNum)
                                                            ? { ...item, sectionName: currentTrack.sectionName ?? currentTrack.nextTrack?.sectionName }
                                                            : item;
                                                    playTrackByItem(withSection, false, { useCommentary: nextCommentary });
                                                }
                                            }
                                        }}
                                        disabled={commentaryCheckboxDisabled}
                                        style={styles.playerCheckboxInput}
                                    />
                                    <span style={commentaryCheckboxDisabled ? styles.playerCheckboxDisabled : undefined}>
                                        С комментарием автора
                                    </span>
                                </label>
                                <label style={styles.playerCheckbox}>
                                    <input
                                        type="checkbox"
                                        checked={playNext}
                                        onChange={() => setPlayNext(!playNext)}
                                        style={styles.playerCheckboxInput}
                                    />
                                    <span>Играть следующий</span>
                                </label>
                            </div>
                            <div style={styles.audioWrap}>
                            <audio
                                ref={audioRef}
                                className="landgraf-audio-player"
                                src={currentTrack.src}
                                controls
                                autoPlay
                                onEnded={() => {
                                    if (currentTrack.nextTrack) {
                                        playTrackByItem(currentTrack.nextTrack, true);
                                    } else if (playNext) {
                                        const endedTrackNum = currentTrack.trackNum;
                                        const idx = trackList.findIndex((t) => t.trackNum === endedTrackNum);
                                        const nextItem = idx >= 0 && idx < trackList.length - 1 ? trackList[idx + 1] : null;
                                        if (nextItem) {
                                            playTrackByItem(nextItem);
                                        } else if (allWorksList.length > 0 && currentWorkIndex >= 0 && currentWorkIndex < allWorksList.length - 1) {
                                            goToNextWork();
                                        } else {
                                            setPlayingTrackNum(null);
                                        }
                                    } else {
                                        setPlayingTrackNum(null);
                                    }
                                }}
                                onPlay={() =>
                                    currentTrack &&
                                    setPlayingTrackNum(
                                        currentTrack.nextTrack
                                            ? currentTrack.nextTrack.trackNum
                                            : currentTrack.trackNum
                                    )
                                }
                                style={styles.audioPlayer}
                            >
                                Ваш браузер не поддерживает аудио.
                            </audio>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleNext}
                            disabled={!canGoNext}
                            style={{
                                ...styles.playerNavBtn,
                                ...(!canGoNext && styles.playerNavBtnDisabled),
                            }}
                            title={allWorksList.length > 0 ? 'Следующий трек / произведение' : 'Следующий трек'}
                        >
                            ›
                        </button>
                    </div>
                </div>
            )}
            {addToPlaylistTrack && (
                <AddToPlaylistModal
                    track={addToPlaylistTrack}
                    onClose={() => setAddToPlaylistTrack(null)}
                    onSuccess={() => markAddedToPlaylist(addToPlaylistTrack)}
                />
            )}
            {showScrollTop && (
                <button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    style={{
                        ...styles.scrollTopBtn,
                        bottom: currentTrack ? 150 : 24,
                    }}
                    title="Наверх"
                    aria-label="Наверх"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                    </svg>
                </button>
            )}
        </Layout>
    );
};

const styles = {
    container: commonStyles.container,
    title: {
        ...commonStyles.title,
        textAlign: 'center',
    },
    backLink: {
        display: 'inline-block',
        marginBottom: theme.spacing.lg,
        color: theme.colors.primary,
        textDecoration: 'none',
        fontSize: theme.fontSize.sm,
    },
    backLinkBtn: {
        display: 'inline-block',
        marginBottom: theme.spacing.md,
        padding: 0,
        border: 'none',
        background: 'none',
        color: theme.colors.primary,
        fontSize: theme.fontSize.sm,
        cursor: 'pointer',
        textAlign: 'left',
    },
    gradeGrid: {
        ...commonStyles.gridCompact,
        marginTop: theme.spacing.xl,
    },
    gradeButton: {
        ...commonStyles.button,
        ...commonStyles.buttonPrimary,
        padding: theme.spacing.lg,
        fontSize: theme.fontSize.xl,
        borderRadius: theme.borderRadius.xl,
    },
    diskGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: theme.spacing.md,
    },
    diskButton: {
        ...commonStyles.button,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.bgTertiary,
        color: theme.colors.textPrimary,
        border: `1px solid ${theme.colors.borderMedium}`,
    },
    diskSwitcherWrap: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing.lg,
    },
    diskSwitcher: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing.xs,
    },
    diskSwitcherBtn: {
        minWidth: 36,
        height: 36,
        padding: '0 8px',
        fontSize: theme.fontSize.sm,
        fontWeight: theme.fontWeight.medium,
        color: theme.colors.textPrimary,
        backgroundColor: theme.colors.bgTertiary,
        border: `1px solid ${theme.colors.borderMedium}`,
        borderRadius: theme.borderRadius.md,
        cursor: 'pointer',
    },
    diskSwitcherArrowBtn: {
        minWidth: 36,
        height: 36,
        padding: '0 10px',
        fontSize: theme.fontSize.lg,
        fontWeight: theme.fontWeight.semibold,
        color: theme.colors.primary,
    },
    diskSwitcherBtnActive: {
        backgroundColor: theme.colors.tableHeaderBlue,
        color: theme.colors.ink,
        border: `1px solid ${theme.colors.ink}`,
    },
    diskSwitcherBtnDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    gradeTitle: {
        fontSize: theme.fontSize['2xl'],
        color: theme.colors.textPrimary,
        marginBottom: theme.spacing.md,
    },
    subtitle: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.lg,
    },
    tableWrapper: {
        ...commonStyles.tableWrapper,
        marginBottom: theme.spacing.xl,
    },
    table: {
        ...commonStyles.table,
        minWidth: '600px',
    },
    th: {
        ...commonStyles.tableHeader,
        border: `1px solid ${theme.colors.borderLight}`,
        padding: theme.spacing.sm,
        whiteSpace: 'nowrap',
    },
    tr: {
        ...commonStyles.tableRow,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    trPlaying: {
        backgroundColor: theme.colors.tableRowAlt,
    },
    td: {
        ...commonStyles.tableCell,
        border: `1px solid ${theme.colors.borderLight}`,
        padding: theme.spacing.sm,
        verticalAlign: 'top',
        whiteSpace: 'normal',
        wordBreak: 'normal',
    },
    tdAuthor: {
        ...commonStyles.tableCell,
        border: `1px solid ${theme.colors.borderLight}`,
        paddingTop: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
        paddingBottom: theme.spacing.sm,
        paddingLeft: theme.spacing.sm,
        verticalAlign: 'top',
        minWidth: '240px',
        whiteSpace: 'normal',
        wordBreak: 'normal',
        overflow: 'visible',
    },
    tdPerformer: {
        ...commonStyles.tableCell,
        border: `1px solid ${theme.colors.borderLight}`,
        padding: theme.spacing.sm,
        verticalAlign: 'top',
        minWidth: '220px',
        fontSize: theme.fontSize.sm,
        whiteSpace: 'normal',
        wordBreak: 'normal',
        overflow: 'visible',
    },
    ...playerBarStyles,
    playerFixedInner: playerBarStyles.playerInner,
    playerFixedContent: playerBarStyles.playerContent,
    audioPlayer: playerBarStyles.audio,
    addPlaylistBtn: {
        padding: '2px 10px',
        minWidth: 28,
        fontSize: theme.fontSize.lg,
        fontWeight: theme.fontWeight.bold,
        lineHeight: 1,
        cursor: 'pointer',
        border: `2px solid ${theme.colors.ink}`,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.parchmentPaper,
        color: theme.colors.ink,
    },
    addPlaylistBtnAdded: {
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: theme.fontSize.sm,
        fontWeight: theme.fontWeight.bold,
        color: theme.colors.success,
    },
    playerCheckboxInput: {
        cursor: 'pointer',
        accentColor: theme.colors.brandTeal,
    },
    scrollTopBtn: {
        position: 'fixed',
        right: 24,
        zIndex: 999,
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        color: theme.colors.textPrimary,
        backgroundColor: theme.colors.bgTertiary,
        border: `1px solid ${theme.colors.borderMedium}`,
        borderRadius: '50%',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        cursor: 'pointer',
    },
};

export default AudioposobiePage;
